import { DateFragment, DateTime, Duration, Utc } from "@tento-chrono";
import { Point } from "@tento-core/point";
import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { StickerDragChip } from "@tento-ui/components/sticker/sticker-drag-chip";
import type { StickerEffect } from "@tento-ui/components/sticker/sticker-effect";
import type { StickerMaterialVariant } from "@tento-ui/components/sticker/sticker-material-variant";
import { type SheetRow } from "@tento-lona";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type {
  StickerItemDelegate,
  StickerSlot,
} from "./sticker-gesture-item.ts";
import { StickerFixedChildren } from "./sticker-fixed-children.ts";

/** Pixels-per-hour the timeline column lays time out at. */
const STICKER_PX_PER_HOUR = 48;
const ZERO: Point = { x: 0, y: 0 };
const SHEET_PREVIEW_ID = "sticker-drag-preview";

/** The drop-target band ($layout) / day-column rect the drop measures
 *  against sits this far BELOW the fixed-child positioning frame the
 *  resting overlay actually renders in (column margin + divider). Without
 *  correcting for it a dropped sticker lands exactly this many px too high.
 *  Calibrated by ux-test: the resting overlay's rendered center sat a
 *  constant 15px above the release point at every drop height until this
 *  was added. */
const BAND_FRAME_OFFSET_PX = 15;

/**
 * The in-flight sticker drag ghost. It owns the existing-sticker MOVE chip
 * while the pointer is outside a sheet drop target, and hands visual tracking
 * to a sheet fixed child while the pointer is inside one. That keeps
 * in-sheet drag previews in the same stacking model as resting stickers and
 * popovers, instead of floating above the app at the document root.
 *
 * Exact landing: a resting sticker is positioned by `inset.top` measured
 * from the fixed-child BAND top (`makeSpec` = COLUMN_TOP + slotOffsetY,
 * centered in its half-slot). So the drop resolves against an explicit
 * `StickerDropZone`: vertical geometry from the band frame, horizontal
 * geometry and `windowed` time mapping from the rendered day column. No DOM
 * ancestry lookup is involved. `StickerFixedChildren.resolveOffsetY` undoes
 * the column-top + half-slot offsets so the settled sticker re-renders
 * centered right where the chip was.
 *
 * Two flows feed it:
 *  - MOVE an existing sticker — the overlay drag source owns the chip
 *    (`startMove`/`moveTo`), the band drop-target persists it (`dropMove`),
 *    and the source's settle (`settle`) keeps the chip + dim overlapping the
 *    resting overlay until it repaints (`flushPendingChip`) so the drop has
 *    no disappear/reappear gap.
 *  - CREATE from the palette — the palette owns the outside-sheet chip; the
 *    target owns the in-sheet preview and this controller resolves + persists
 *    the drop (`previewCreate`/`dropCreate`).
 */
export class StickerDragGhost {
  #move: Option<MoveDrag> = null;
  #sheetPreview: Option<SheetPreview> = null;
  /** A just-settled move's chip + dimmed overlay, kept alive until the
   *  resting overlay repaints (flushed by the plugin's post-persist re-emit)
   *  so the drop has no disappear/reappear gap. */
  #pending: Option<{ chip: StickerDragChip; dragged: HTMLElement }> = null;

  readonly #delegate: () => Option<StickerItemDelegate>;
  constructor(delegate: () => Option<StickerItemDelegate>) {
    this.#delegate = delegate;
  }

  // ---- MOVE flow: an existing sticker's overlay is the drag source ----

  /** Begin a MOVE: float a chip for `slot`, grabbed at `grab` (offset from
   *  the artwork center) so the grabbed point tracks the cursor. `liftClient`
   *  is the pointer position at lift, so the chip appears exactly over the
   *  resting sticker (seamless hand-off). `dragged` is that resting overlay,
   *  dimmed in place while the chip stands in. */
  startMove(
    row: SheetRow.Joined.Handle,
    slot: StickerSlot,
    grab: Point,
    liftClient: Point,
    dragged: HTMLElement,
  ): void {
    this.#discard();
    dragged.style.opacity = "0.35";
    const chip = StickerDragChip.art(slot.stickerId, slot.effect, null, grab);
    chip.moveTo(liftClient);
    this.#move = { row, slot, grab, chip, dragged, persisted: false };
  }

  /** Track the pointer (viewport coords). */
  moveTo(client: Point, visible: boolean): void {
    const m = this.#move;
    if (!m) return;
    m.chip.setVisible(visible);
    m.chip.moveTo(client);
  }

  /** Show the MOVE preview inside the sheet-owned fixed-child layer while
   *  the pointer is over a compatible drop target. */
  previewMove(
    handle: Option<SheetDelegate>,
    dropZone: StickerDropZone,
    client: Point,
  ): void {
    const m = this.#move;
    if (!m) return;
    m.chip.setVisible(false);
    this.#showSheetPreview(
      handle,
      dropZone,
      `move:${m.slot.cellId}:${m.slot.stickerId}:${m.slot.effect}`,
      {
        stickerId: m.slot.stickerId,
        effect: m.slot.effect,
        effectVariant: null,
        x: 0.5,
      },
      client,
      m.grab,
    );
  }

  /** Persist a MOVE at the EXACT release position over a sticker drop zone. */
  dropMove(
    dropZone: Option<StickerDropZone>,
    releaseClient: Point,
  ): void {
    const m = this.#move;
    if (!m) return;
    const resolved = this.#resolve(dropZone, releaseClient, m.grab);
    if (!resolved) return;
    const delegate = this.#delegate();
    if (!delegate) return;
    delegate.setContext(m.row);
    void delegate.persistTimeChange(m.slot, resolved.range, resolved.xFraction);
    m.persisted = true;
  }

  /** Source settle (after any drop). If the move persisted, KEEP the chip +
   *  dim overlapping the resting overlay — the plugin flushes them once it
   *  repaints, so there's no gap. Otherwise REVERT immediately: un-dim, drop
   *  the chip. */
  settle(): void {
    const m = this.#move;
    if (!m) return;
    this.clearSheetPreview();
    this.#move = null;
    if (m.persisted) {
      this.#pending?.chip.remove();
      this.#pending = { chip: m.chip, dragged: m.dragged };
    } else {
      m.chip.remove();
      m.dragged.style.opacity = "";
    }
  }

  /** Remove the just-settled chip + un-dim the moved overlay once it has
   *  repainted — called by the plugin from its post-persist overlay re-emit.
   *  Deferred one frame so the chip and the resting sticker overlap for one
   *  paint (no disappear/reappear gap). */
  flushPendingChip(): void {
    const p = this.#pending;
    if (!p) return;
    this.#pending = null;
    requestAnimationFrame(() => {
      p.chip.remove();
      p.dragged.style.opacity = "";
    });
  }

  #discard(): void {
    this.clearSheetPreview();
    if (this.#move) {
      this.#move.chip.remove();
      this.#move.dragged.style.opacity = "";
      this.#move = null;
    }
    if (this.#pending) {
      this.#pending.chip.remove();
      this.#pending.dragged.style.opacity = "";
      this.#pending = null;
    }
  }

  // ---- CREATE flow: the palette owns its own floating chip ----

  /** Persist a new palette sticker at the EXACT release position (the
   *  palette chip is centered, so grab is zero). */
  dropCreate(
    stickerId: string,
    effect: StickerEffect.Id,
    dropZone: Option<StickerDropZone>,
    releaseClient: Point,
  ): void {
    const resolved = this.#resolve(dropZone, releaseClient, ZERO);
    if (!resolved) return;
    this.#delegate()?.onStickerDropped(
      stickerId,
      effect,
      resolved.range,
      resolved.xFraction,
    );
  }

  /** Show the CREATE preview inside the sheet-owned fixed-child layer while
   *  the pointer is over a compatible drop target. */
  previewCreate(
    handle: Option<SheetDelegate>,
    dropZone: StickerDropZone,
    stickerId: string,
    effect: StickerEffect.Id,
    effectVariant: Option<StickerMaterialVariant.Id>,
    client: Point,
  ): void {
    this.#showSheetPreview(
      handle,
      dropZone,
      `create:${stickerId}:${effect}:${effectVariant ?? ""}`,
      { stickerId, effect, effectVariant, x: 0.5 },
      client,
      ZERO,
    );
  }

  clearSheetPreview(): void {
    const preview = this.#sheetPreview;
    if (!preview) return;
    this.#discardSheetPreview(preview);
    this.#move?.chip.setVisible(true);
  }

  // ---- shared ----

  #showSheetPreview(
    handle: Option<SheetDelegate>,
    dropZone: StickerDropZone,
    key: string,
    visual: StickerFixedChildren.Visual,
    client: Point,
    grab: Point,
  ): void {
    if (!handle) return;
    const inset = this.#previewInset(dropZone, client, grab);
    const rowIdKey = String(dropZone.bandRowId);
    const preview = this.#sheetPreview;
    if (preview && preview.key === key && preview.rowIdKey === rowIdKey) {
      const xUpdated = handle.fixedChild.repositionFixedChild(
        SHEET_PREVIEW_ID,
        dropZone.column,
        inset.left,
      );
      const yUpdated = handle.fixedChild.updateFixedChildInset(
        SHEET_PREVIEW_ID,
        inset,
        dropZone.bandRowId,
      );
      if (xUpdated && yUpdated) return;
      this.#discardSheetPreview(preview);
    } else if (preview) {
      this.#discardSheetPreview(preview);
    }

    const $wrap = makeSheetPreviewWrap();
    StickerFixedChildren.renderFloatingVisual($wrap, visual, "raised");
    handle.fixedChild.reveal(
      SHEET_PREVIEW_ID,
      $wrap,
      { x: dropZone.column, y: dropZone.bandRowId },
      {
        animate: false,
        inset,
        widthBehavior: 0,
      },
    );
    this.#sheetPreview = { key, rowIdKey, handle };
  }

  #previewInset(
    dropZone: StickerDropZone,
    client: Point,
    grab: Point,
  ): { left: number; top: number } {
    const bandRect = dropZone.bandRect();
    const colRect = dropZone.columnRect();
    const anchorX = client.x - grab.x;
    const anchorY = client.y - grab.y;
    return {
      left: anchorX - colRect.left,
      top: anchorY - bandRect.top + BAND_FRAME_OFFSET_PX,
    };
  }

  #discardSheetPreview(preview: SheetPreview): void {
    this.#sheetPreview = null;
    preview.handle.fixedChild.dismiss(SHEET_PREVIEW_ID, "instant");
  }

  /** Resolve a release point to the (time, x) whose RESTING render lands the
   *  sticker's center exactly at the chip (anchor = pointer − grab). */
  #resolve(
    dropZone: Option<StickerDropZone>,
    client: Point,
    grab: Point,
  ): Option<{ range: DateTime.Range<Utc>; xFraction: number }> {
    if (!dropZone) return null;
    const windowed = dropZone.windowed;
    const bandRect = dropZone.bandRect();
    const colRect = dropZone.columnRect();
    const anchorX = client.x - grab.x;
    const anchorY = client.y - grab.y;
    const offsetX = anchorX - colRect.left;
    const xFraction = colRect.width > 0
      ? Math.max(0, Math.min(1, offsetX / colRect.width))
      : 0.5;
    // Vertical: measured in the BAND frame (where `makeSpec` places the
    // resting overlay), then undo its column-top + half-slot centering so
    // the persisted sticker re-renders centered at the chip — no drop shift.
    // `+ BAND_FRAME_OFFSET_PX` lifts the measured band rect into the actual
    // fixed-child frame (see the constant).
    const bandCenterY = anchorY - bandRect.top + BAND_FRAME_OFFSET_PX;
    const offsetY = StickerFixedChildren.resolveOffsetY(bandCenterY);
    const tod = new CalendarColumnCell.PointerPosition(
      { x: offsetX, y: offsetY },
      null,
      xFraction,
    ).resolve(windowed, STICKER_PX_PER_HOUR);
    const start = windowed.fragment.start.withTime(tod.toTimeOfDayWrapping());
    const range = new DateTime.Range(
      start,
      start.add(Duration.Time.mins(30)),
    ).toUtc();
    return { range, xFraction };
  }
}

export interface StickerDropZone {
  readonly windowed: DateFragment.Windowed;
  readonly bandRowId: RowLocalId;
  readonly column: ColumnIndex.Key.Resolved.WithSubindex;
  bandRect(): DOMRect;
  columnRect(): DOMRect;
}

interface SheetPreview {
  key: string;
  rowIdKey: string;
  handle: SheetDelegate;
}

interface MoveDrag {
  row: SheetRow.Joined.Handle;
  slot: StickerSlot;
  /** Offset of the grab from the artwork center, so the grabbed point
   *  tracks the cursor and the drop resolves to the same anchor. */
  grab: Point;
  chip: StickerDragChip;
  /** The resting overlay element being dragged — dimmed during the drag,
   *  un-dimmed when the move repaints (or on revert). */
  dragged: HTMLElement;
  /** Set by `dropMove`: did the settle persist (→ overlap until repaint) or
   *  not (→ revert immediately)? */
  persisted: boolean;
}

function makeSheetPreviewWrap(): HTMLElement {
  const $wrap = document.createElement("div");
  $wrap.style.position = "relative";
  $wrap.style.width = "0px";
  $wrap.style.height = "0px";
  $wrap.style.overflow = "visible";
  $wrap.style.pointerEvents = "none";
  $wrap.style.zIndex = "var(--sticker-raised-z-index, 16777267)";
  $wrap.setAttribute("data-sticker-drag-preview", "true");
  return $wrap;
}
